import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Fab,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  IconButton,
  TextField,
  Tooltip,
  Divider,
  Collapse,
  Button,
  Pagination
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ShareIcon from '@mui/icons-material/Share';
import SyncIcon from '@mui/icons-material/Sync';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Pedido, StatusPedido } from '@azoupvendas/shared';
import { podeExcluirPedido } from '@azoupvendas/shared';
import api from '@/services/auth.service';
import { useAuth } from '@/hooks/useAuth';
import { ShareModal } from '../components/ShareModal';
import { SyncStatusBadge } from '../components/SyncStatusBadge';
import { pedidoClientService } from '../services/pedido.service';
import { ExcluirPedidoDialog } from '../components/ExcluirPedidoDialog';

// AIDEV-NOTE: Helpers de formatacao consistentes com PedidoDetailPage
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
}

// AIDEV-NOTE: Strings date-only (YYYY-MM-DD) do Firebird nao passam por UTC para evitar -1 dia em UTC-3
function formatDate(dateStr: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const parts = dateStr.split('-');
    const y = parseInt(parts[0] ?? '0', 10);
    const m = parseInt(parts[1] ?? '1', 10);
    const d = parseInt(parts[2] ?? '1', 10);
    return new Date(y, m - 1, d).toLocaleDateString('pt-BR');
  }
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

// AIDEV-NOTE: Configuracao de badges de status — mesma usada em PedidoDetailPage
const STATUS_CONFIG: Record<
  StatusPedido,
  { label: string; color: 'default' | 'primary' | 'warning' | 'success' | 'error' }
> = {
  rascunho: { label: 'Rascunho', color: 'default' },
  finalizado: { label: 'Finalizado', color: 'primary' },
  sincronizando: { label: 'Sincronizando', color: 'warning' },
  sincronizado: { label: 'Sincronizado', color: 'success' },
  erro_sync: { label: 'Erro Sync', color: 'error' }
};

// AIDEV-NOTE: Status do ERP sincronizado de volta do Firebird (CARTEIRA_PEDIDO.STATUS)
const STATUS_ERP_CONFIG: Record<
  string,
  { label: string; color: 'default' | 'primary' | 'warning' | 'success' | 'error' }
> = {
  P: { label: 'Em análise', color: 'success' },
  A: { label: 'Aprovado', color: 'primary' },
  R: { label: 'Romaneado', color: 'default' },
  F: { label: 'Faturado', color: 'warning' },
  C: { label: 'Cancelado', color: 'error' }
};

const STATUS_OPTIONS: StatusPedido[] = [
  'rascunho',
  'finalizado',
  'sincronizando',
  'sincronizado',
  'erro_sync'
];

interface PedidosApiResponse {
  pedidos: Pedido[];
  total?: number;
}

const PAGE_SIZE = 20;

// AIDEV-NOTE: erro COM response = recusa do servidor (propaga para a UI mostrar o motivo).
// Erro SEM response = rede/timeout — ai vale cair no que esta salvo no aparelho.
function temRespostaDoServidor(err: unknown): boolean {
  return typeof err === 'object' && err !== null && !!(err as { response?: unknown }).response;
}

// AIDEV-NOTE: Lista de pedidos com paginacao server-side, filtros com debounce e FAB para novo pedido.
// Botoes de acao nos cards: sincronizar (manual), compartilhar, editar (rascunho).
export function PedidosPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Filtros (valores imediatos — digitados pelo usuario)
  const [statusFiltro, setStatusFiltro] = useState<StatusPedido | null>(null);
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroNumero, setFiltroNumero] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');

  // AIDEV-NOTE: Valores debouncados (400ms) para nao disparar request a cada tecla
  const [debouncedCliente, setDebouncedCliente] = useState('');
  const [debouncedNumero, setDebouncedNumero] = useState('');

  const [page, setPage] = useState(1);

  // Estado dos cards
  const [shareTarget, setShareTarget] = useState<Pedido | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [loadingShareId, setLoadingShareId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Pedido | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [syncFeedback, setSyncFeedback] = useState<{
    severity: 'info' | 'error';
    message: string;
  } | null>(null);

  // Debounce para texto livre — 400ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedCliente(filtroCliente), 400);
    return () => clearTimeout(t);
  }, [filtroCliente]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedNumero(filtroNumero), 400);
    return () => clearTimeout(t);
  }, [filtroNumero]);

  // Volta para página 1 quando qualquer filtro muda
  useEffect(() => {
    setPage(1);
  }, [statusFiltro, debouncedCliente, debouncedNumero, filtroDataInicio, filtroDataFim]);

  const {
    data: response,
    isLoading,
    error
  } = useQuery({
    queryKey: [
      'pedidos',
      statusFiltro,
      debouncedCliente,
      debouncedNumero,
      filtroDataInicio,
      filtroDataFim,
      page
    ],
    staleTime: 30_000,
    // AIDEV-NOTE: Substitui o realtime do Supabase (removido do projeto — usePedidoRealtime esta
    // morto): enquanto houver pedido em 'sincronizando', repesca a cada 5s para o status sair
    // sozinho de "Sincronizando" para "Sincronizado"/"Erro Sync".
    refetchInterval: (query) =>
      query.state.data?.pedidos?.some((p) => p.status === 'sincronizando') ? 5_000 : false,
    queryFn: async (): Promise<PedidosApiResponse> => {
      const params: Record<string, string | number> = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE
      };
      if (statusFiltro) params.status = statusFiltro;
      if (debouncedCliente.trim()) params.clienteNome = debouncedCliente.trim();
      if (debouncedNumero.trim()) params.numero = parseInt(debouncedNumero, 10);
      if (filtroDataInicio) params.data_inicio = filtroDataInicio;
      if (filtroDataFim) params.data_fim = filtroDataFim;
      // AIDEV-NOTE: pedidos que ainda nao subiram vivem SO no Dexie. Sem mescla-los aqui, o pedido
      // fechado sem sinal sumia da lista ate o drain rodar, e o vendedor concluia que tinha
      // perdido o trabalho digitado — era exatamente a queixa de campo (2026-10). Eles entram no
      // topo (mais recentes) e so na primeira pagina, para nao bagunçar a paginacao do servidor.
      const locais = await pedidoClientService.listarPedidosLocaisPendentes(user?.vendedor_id ?? 0);
      const locaisFiltrados = statusFiltro
        ? locais.filter((p) => p.status === statusFiltro)
        : locais;

      try {
        const res = await api.get<PedidosApiResponse>('/pedidos', { params });
        if (page > 1 || locaisFiltrados.length === 0) return res.data;

        const idsApi = new Set(res.data.pedidos.map((p) => p.id));
        const pendentes = locaisFiltrados.filter((p) => !idsApi.has(p.id));
        return {
          pedidos: [...pendentes, ...res.data.pedidos],
          total: (res.data.total ?? res.data.pedidos.length) + pendentes.length
        };
      } catch (err) {
        // Sem resposta do servidor: mostra o que existe no aparelho em vez de uma tela de erro
        if (temRespostaDoServidor(err)) throw err;
        return { pedidos: locaisFiltrados, total: locaisFiltrados.length };
      }
    }
  });

  const pedidos = response?.pedidos ?? [];
  const total = response?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleStatusToggle = (status: StatusPedido) => {
    setStatusFiltro((prev) => (prev === status ? null : status));
  };

  // AIDEV-NOTE: Sincroniza pedido individual via POST /sync/pedido/:id
  const handleSyncPedido = useCallback(
    async (e: React.MouseEvent, pedidoId: string) => {
      e.stopPropagation();
      setSyncingId(pedidoId);
      setSyncFeedback(null);
      try {
        await api.post(`/sync/pedido/${pedidoId}`);
        void queryClient.invalidateQueries({ queryKey: ['pedidos'] });
        // AIDEV-NOTE: a API responde assim que ENFILEIRA o push (nao quando ele termina) — por isso
        // a mensagem fala em "iniciada" e a lista passa a fazer polling enquanto houver
        // 'sincronizando'. Antes o catch era vazio e o botao nao dava sinal nenhum de vida.
        setSyncFeedback({
          severity: 'info',
          message: 'Sincronização iniciada. O status é atualizado automaticamente.'
        });
      } catch (err) {
        const message =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          (err instanceof Error ? err.message : 'Erro ao sincronizar');
        setSyncFeedback({ severity: 'error', message });
      } finally {
        setSyncingId(null);
      }
    },
    [queryClient]
  );

  // AIDEV-NOTE: Busca pedido completo (com itens/parcelas) antes de abrir o ShareModal
  // A lista não retorna itens/parcelas, então o PDF quebraria sem essa busca
  const handleShareClick = useCallback(async (e: React.MouseEvent, pedido: Pedido) => {
    e.stopPropagation();
    setLoadingShareId(pedido.id);
    try {
      const response = await api.get<{ pedido: Pedido }>(`/pedidos/${pedido.id}`);
      setShareTarget(response.data.pedido);
    } catch {
      setShareTarget(pedido);
    } finally {
      setLoadingShareId(null);
    }
  }, []);

  const handleEditClick = useCallback(
    (e: React.MouseEvent, pedidoId: string) => {
      e.stopPropagation();
      navigate(`/pedidos/${pedidoId}/editar`);
    },
    [navigate]
  );

  const handleDeleteClick = useCallback((e: React.MouseEvent, pedido: Pedido) => {
    e.stopPropagation();
    setDeleteTarget(pedido);
  }, []);

  // AIDEV-NOTE: Exclusao offline-first — service decide entre DELETE na API ou op_log (Dexie)
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await pedidoClientService.excluirPedido(deleteTarget.id, user?.vendedor_id ?? 0);
      setDeleteTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['pedidos'] });
    } catch (err) {
      console.error('Erro ao excluir pedido:', err);
      setDeleteError('Não foi possível excluir o pedido. Tente novamente.');
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, queryClient, user?.vendedor_id]);

  const renderPedidoCard = (pedido: Pedido) => {
    const isOffline = !pedido.numero;
    const canSync = pedido.status === 'finalizado' || pedido.status === 'erro_sync';
    const canShare = pedido.status !== 'rascunho';
    // AIDEV-NOTE: Permite editar rascunho + finalizado/erro_sync sem numero (nao sincronizados)
    const canEdit =
      pedido.status === 'rascunho' ||
      ((pedido.status === 'finalizado' || pedido.status === 'erro_sync') && !pedido.numero);
    // AIDEV-NOTE: rascunho + finalizado/erro_sync sem numero — regra unica em @shared,
    // a mesma que a guarda do servidor aplica (pedidos.repository.deletePedido)
    const canDelete = podeExcluirPedido(pedido);
    const isSyncing = syncingId === pedido.id;

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 1,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
          transition: 'background-color 0.15s'
        }}
        onClick={() => navigate(`/pedidos/${pedido.id}`)}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body1" fontWeight={600} noWrap>
                {pedido.numero ? `Pedido N. ${pedido.numero}` : 'Pedido (Rascunho)'}
              </Typography>
              {isOffline && (
                <Chip label="OFFLINE" size="small" color="warning" variant="outlined" />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" noWrap>
              {pedido.cliente_nome}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {pedido.created_at ? `App: ${formatDate(pedido.created_at)}` : ''}
              {pedido.data_pedido_erp && ` · ERP: ${formatDate(pedido.data_pedido_erp)}`}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'right', ml: 2, flexShrink: 0 }}>
            <Typography variant="body1" fontWeight={700} color="primary.main">
              {formatCurrency(pedido.total_final)}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                justifyContent: 'flex-end',
                mt: 0.5,
                flexWrap: 'wrap'
              }}
            >
              {/* AIDEV-NOTE: SyncStatusBadge (e não Chip cru) — traz o tooltip com a mensagem de
                  erro, igual ao PedidoDetailPage. A mensagem completa aparece abaixo do card. */}
              <SyncStatusBadge status={pedido.status} syncError={pedido.sync_error ?? null} />
              {(() => {
                const erpCfg = STATUS_ERP_CONFIG[(pedido as any).status_erp as string];
                return erpCfg ? (
                  <Chip
                    label={`ERP: ${erpCfg.label}`}
                    color={erpCfg.color}
                    size="small"
                    variant="outlined"
                  />
                ) : null;
              })()}
            </Box>
          </Box>
        </Box>

        {/* AIDEV-NOTE: Mensagem de erro no proprio card — o tooltip do badge depende de hover, que
            nao existe no mobile; sem isto o vendedor via "Erro Sync" sem nenhuma explicacao. */}
        {pedido.status === 'erro_sync' && pedido.sync_error && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: 'block', mt: 1, wordBreak: 'break-word' }}
          >
            {pedido.sync_error}
          </Typography>
        )}

        {/* Acoes do card */}
        {(canSync || canShare || canEdit || canDelete) && (
          <>
            <Divider sx={{ mt: 1, mb: 0.5 }} />
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
              {canEdit && (
                <Tooltip title={pedido.status === 'rascunho' ? 'Editar rascunho' : 'Editar pedido'}>
                  <IconButton size="small" onClick={(e) => handleEditClick(e, pedido.id)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canSync && (
                <Tooltip
                  title={pedido.status === 'erro_sync' ? 'Tentar novamente' : 'Sincronizar com ERP'}
                >
                  <IconButton
                    size="small"
                    color={pedido.status === 'erro_sync' ? 'error' : 'primary'}
                    onClick={(e) => handleSyncPedido(e, pedido.id)}
                    disabled={isSyncing}
                  >
                    {isSyncing ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
              {canShare && (
                <Tooltip title="Compartilhar">
                  <IconButton
                    size="small"
                    onClick={(e) => handleShareClick(e, pedido)}
                    disabled={loadingShareId === pedido.id}
                  >
                    {loadingShareId === pedido.id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <ShareIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip
                  title={pedido.status === 'rascunho' ? 'Excluir rascunho' : 'Excluir pedido'}
                >
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={
                      pedido.status === 'rascunho' ? 'Excluir rascunho' : 'Excluir pedido'
                    }
                    onClick={(e) => handleDeleteClick(e, pedido)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </>
        )}
      </Paper>
    );
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // AIDEV-NOTE: Filtros ocultos por padrão no mobile para ganhar espaço de tela
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  const vendedorData = { nome: user?.nome ?? 'Vendedor' };
  const empresaData = { nome_fantasia: 'AzoupVendas', cnpj: '' };

  const temFiltroAtivo = !!(
    statusFiltro ||
    filtroCliente ||
    filtroNumero ||
    filtroDataInicio ||
    filtroDataFim
  );

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Título + contador + botão filtros no mobile */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="h5">Pedidos</Typography>
          {total > 0 && (
            <Typography variant="caption" color="text.secondary">
              {total} {total === 1 ? 'resultado' : 'resultados'}
            </Typography>
          )}
        </Box>
        {isMobile && (
          <Button
            size="small"
            variant={temFiltroAtivo ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />}
            onClick={() => setFiltrosAbertos((v) => !v)}
            sx={{ minWidth: 0, px: 1.5 }}
          >
            Filtros{temFiltroAtivo ? ' •' : ''}
          </Button>
        )}
      </Box>

      {/* Filtros — colapsáveis no mobile, sempre visíveis no desktop */}
      <Collapse in={!isMobile || filtrosAbertos}>
        <Box sx={{ mb: 1.5 }}>
          {/* Chips de status */}
          <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
            {STATUS_OPTIONS.map((status) => {
              const cfg = STATUS_CONFIG[status];
              const isActive = statusFiltro === status;
              return (
                <Chip
                  key={status}
                  label={cfg.label}
                  color={isActive ? cfg.color : 'default'}
                  variant={isActive ? 'filled' : 'outlined'}
                  onClick={() => handleStatusToggle(status)}
                  size="small"
                />
              );
            })}
          </Stack>

          {/* Campos de texto — coluna no mobile, linha no desktop */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
            <TextField
              label="Cliente"
              size="small"
              value={filtroCliente}
              onChange={(e) => setFiltroCliente(e.target.value)}
              sx={{ flex: 1, minWidth: { sm: 160 } }}
              placeholder="Buscar por nome..."
            />
            <TextField
              label="Pedido"
              size="small"
              type="number"
              value={filtroNumero}
              onChange={(e) => setFiltroNumero(e.target.value)}
              sx={{ width: { xs: '100%', sm: 110 } }}
              inputProps={{ min: 1 }}
            />
            <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
              <TextField
                label="Data início"
                size="small"
                type="date"
                value={filtroDataInicio}
                onChange={(e) => setFiltroDataInicio(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Data fim"
                size="small"
                type="date"
                value={filtroDataFim}
                onChange={(e) => setFiltroDataFim(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Stack>
          </Stack>
        </Box>
      </Collapse>

      {/* Feedback do botao sincronizar do card */}
      {syncFeedback && (
        <Alert
          severity={syncFeedback.severity}
          onClose={() => setSyncFeedback(null)}
          sx={{ mb: 1.5 }}
        >
          {syncFeedback.message}
        </Alert>
      )}

      {/* Erro de exclusão */}
      {deleteError && (
        <Alert severity="error" onClose={() => setDeleteError(null)} sx={{ mb: 1.5 }}>
          {deleteError}
        </Alert>
      )}

      {/* Loading */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Erro */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Erro ao carregar pedidos. Tente novamente.
        </Alert>
      )}

      {/* Lista vazia */}
      {!isLoading && !error && pedidos.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Nenhum pedido encontrado.
          </Typography>
        </Box>
      )}

      {/* Lista de pedidos */}
      {!isLoading && pedidos.length > 0 && (
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {pedidos.map((pedido) => (
            <Box key={pedido.id}>{renderPedidoCard(pedido)}</Box>
          ))}
        </Box>
      )}

      {/* Paginação */}
      {!isLoading && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 0.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="small"
            siblingCount={1}
          />
        </Box>
      )}

      {/* FAB para novo pedido */}
      <Fab
        color="primary"
        aria-label="Novo pedido"
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
        onClick={() => navigate('/pedidos/novo')}
      >
        <AddIcon />
      </Fab>

      {/* Confirmação de exclusão — rascunho ou pedido finalizado ainda não sincronizado */}
      <ExcluirPedidoDialog
        open={!!deleteTarget}
        loading={isDeleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        pedidoFinalizado={!!deleteTarget && deleteTarget.status !== 'rascunho'}
      />

      {/* ShareModal controlado pelo shareTarget */}
      {shareTarget && (
        <ShareModal
          open={true}
          onClose={() => setShareTarget(null)}
          pedido={shareTarget}
          empresa={empresaData}
          vendedor={vendedorData}
        />
      )}
    </Box>
  );
}
